import cidrRegex from 'cidr-regex';
import ipRegex from 'ip-regex';

/**
 * Compresses (for IPv6) then truncates an address for display,
 * preserving any /cidr and :port suffixes.
 */
export function truncateAddress(ip: string): string {
  // Compress IPv6 first, then truncate the result.
  const compressed = compressAddress(ip);
  if (compressed.length <= 20) return compressed;

  // CIDR — strip the prefix length, truncate the address, re-append.
  if (cidrRegex({ exact: true }).test(compressed)) {
    const slash = compressed.lastIndexOf('/');
    const addr = compressed.slice(0, slash);
    const suffix = compressed.slice(slash);
    const truncFn = ipRegex.v6({ exact: true }).test(addr) ? truncateIPv6 : truncate;
    return `${truncFn(addr)}${suffix}`;
  }

  // Bare IPv6 (already compressed above).
  if (ipRegex.v6({ exact: true }).test(compressed)) {
    return truncateIPv6(compressed);
  }

  // IPv4, domain, or [IPv6] — with or without :port. Now that bare IPv6 is excluded,
  // any :digits suffix is unambiguously a port.
  const lastColon = compressed.lastIndexOf(':');
  if (lastColon > 0 && /^\d+$/.test(compressed.slice(lastColon + 1))) {
    const base = compressed.slice(0, lastColon);
    const port = compressed.slice(lastColon + 1);
    // For [IPv6]:port, strip brackets, truncate the inner address, restore brackets.
    if (base.startsWith('[') && base.endsWith(']')) {
      return `[${truncateIPv6(base.slice(1, -1))}]:${port}`;
    }
    return `${truncate(base)}:${port}`;
  }

  // Plain IPv4 or domain.
  return truncate(compressed);
}

function truncate(s: string): string {
  if (s.length <= 20) return s;
  return `${s.slice(0, 13)}...${s.slice(-4)}`;
}

/** Truncate a compressed IPv6 address after its first group. */
function truncateIPv6(s: string): string {
  if (s.length <= 20) return s;
  const firstColon = s.indexOf(':');
  if (firstColon === -1) return truncate(s);
  // firstGroup + ":..." = firstColon + 4 chars; fill the rest from the end
  const tail = 20 - firstColon - 4;
  if (tail <= 0) return truncate(s);
  return `${s.slice(0, firstColon)}:...${s.slice(-tail)}`;
}

/**
 * Compresses an IPv6 address using standard shortening rules:
 * 1. Strip leading zeros from each group (00c0 -> c0)
 * 2. Collapse the longest run of consecutive all-zero groups with ::
 */
function compressIPv6(addr: string): string {
  // Split into groups (handles both full and already-partial addresses)
  const groups = addr.split(':');

  // Strip leading zeros from each group
  const stripped = groups.map((g) => g.replace(/^0+(.)/, '$1'));

  // Find the longest run of consecutive "0" groups for :: collapse
  let bestStart = -1;
  let bestLen = 0;
  let curStart = -1;
  let curLen = 0;
  for (let i = 0; i < stripped.length; i++) {
    if (stripped[i] === '0') {
      if (curStart === -1) curStart = i;
      curLen++;
      if (curLen > bestLen) {
        bestStart = curStart;
        bestLen = curLen;
      }
    } else {
      curStart = -1;
      curLen = 0;
    }
  }

  if (bestLen >= 2) {
    const before = stripped.slice(0, bestStart).join(':');
    const after = stripped.slice(bestStart + bestLen).join(':');
    return `${before}::${after}`;
  }

  return stripped.join(':');
}

/**
 * Compresses an address without any string truncation.
 * Applies IPv6 shortening (strip leading zeros, collapse zero runs with ::)
 * but leaves non-IPv6 addresses unchanged.
 */
export function compressAddress(ip: string): string {
  // IPv6 CIDR
  if (cidrRegex({ exact: true }).test(ip)) {
    const slash = ip.lastIndexOf('/');
    const addr = ip.slice(0, slash);
    const suffix = ip.slice(slash);
    if (ipRegex.v6({ exact: true }).test(addr)) {
      return `${compressIPv6(addr)}${suffix}`;
    }
    return ip;
  }

  // Bare IPv6
  if (ipRegex.v6({ exact: true }).test(ip)) {
    return compressIPv6(ip);
  }

  return ip;
}
